/**
 * Theme Context
 * =============
 * 
 * Provides global theme state management for the application.
 * 
 * USAGE:
 * ```jsx
 * import { useTheme } from '@/contexts/ThemeContext';
 * 
 * function MyComponent() {
 *   const { theme, setTheme, toggleTheme, isDark } = useTheme();
 *   
 *   return (
 *     <button onClick={toggleTheme}>
 *       Current: {theme}
 *     </button>
 *   );
 * }
 * ```
 * 
 * The theme is persisted in localStorage and applied to the document root.
 * Components can use CSS variables that automatically update with theme changes.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { THEMES, DEFAULT_THEME } from '@/config/theme';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'macromeal_theme';

/**
 * Get the initial theme from localStorage or system preference
 */
const getInitialTheme = () => {
  // Check localStorage first
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && Object.values(THEMES).includes(stored)) {
        return stored;
      }
    } catch (e) {
      console.warn('[Theme] Could not read from localStorage');
    }

    // Future: Check system preference
    // if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    //   return THEMES.DARK;
    // }
  }

  return DEFAULT_THEME;
};

/**
 * Apply theme to the document
 * Sets data-theme attribute on html element for CSS variable switching
 */
const applyThemeToDocument = (theme) => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    
    // Set data-theme attribute (used by CSS for variable switching)
    root.setAttribute('data-theme', theme);
    
    // Also set class for Tailwind dark mode compatibility
    if (theme === THEMES.DARK) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === THEMES.DARK ? '#111111' : '#FFFFFF'
      );
    }
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      theme: DEFAULT_THEME,
      setTheme: () => {},
      toggleTheme: () => {},
      isDark: false,
      isLight: true,
      themes: THEMES,
    };
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [mounted, setMounted] = useState(false);

  // Apply theme on mount and when it changes
  useEffect(() => {
    setMounted(true);
    applyThemeToDocument(theme);
  }, [theme]);

  // Persist theme to localStorage
  const setTheme = useCallback((newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.warn(`[Theme] Invalid theme: ${newTheme}`);
      return;
    }

    setThemeState(newTheme);
    
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch (e) {
      console.warn('[Theme] Could not persist to localStorage');
    }
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setTheme(theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
  }, [theme, setTheme]);

  // Derived state
  const isDark = theme === THEMES.DARK;
  const isLight = theme === THEMES.LIGHT;

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme,
    isDark,
    isLight,
    themes: THEMES,
    mounted,
  }), [theme, setTheme, toggleTheme, isDark, isLight, mounted]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
